import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './NewDashboard.css';
import './AdminDashboard.css';
import regionalApiService from '../services/RegionalApiService';
import ActiveRegionalBadge from './common/ActiveRegionalBadge';
import { useAuth } from '../contexts/AuthContext';
import { isAdminArea, isAdminRegional } from '../utils/adminMenu';

const AdminCreateRegional = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const isRegionalAdmin = isAdminRegional(user);
  const isAreaAdmin = isAdminArea(user);
  const isGeneralAdmin = !isRegionalAdmin && !isAreaAdmin;

  const [form, setForm] = useState({
    nombre: '',
    sigla: '',
    cuit: '',
    descripcion: '',
    telefono: '',
    email: '',
    apiKeyOpenAI: '',
    apiKeyRagFlow: '',
    responsableNombre: '',
    responsableApellido: '',
    responsableEmail: '',
    direccionCalle: '',
    direccionNumero: '',
    ciudad: '',
    provincia: '',
    codigoPostal: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [regionales, setRegionales] = useState([]);
  const [regionalesLoading, setRegionalesLoading] = useState(false);
  const [regionalesError, setRegionalesError] = useState('');

  useEffect(() => {
    if (!isGeneralAdmin) {
      navigate('/admin/select-regional', { replace: true });
    }
  }, [isGeneralAdmin, navigate]);

  useEffect(() => {
    const fetchRegionales = async () => {
      setRegionalesLoading(true);
      setRegionalesError('');
      try {
        const resp = await regionalApiService.getRegionales();
        const list = resp?.regionales || resp?.Regionales || resp?.data || resp || [];
        setRegionales(Array.isArray(list) ? list : []);
      } catch (error) {
        setRegionalesError(error.message || 'No pudimos cargar los regionales.');
      } finally {
        setRegionalesLoading(false);
      }
    };

    if (isGeneralAdmin) {
      fetchRegionales();
    }
  }, [isGeneralAdmin]);

  const formatCuit = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    const parts = [];
    if (digits.length > 0) {
      parts.push(digits.slice(0, 2));
    }
    if (digits.length > 2) {
      parts.push(digits.slice(2, 10));
    }
    if (digits.length > 10) {
      parts.push(digits.slice(10, 11));
    }
    return parts.join('-');
  };

  const handleChange = (field, value) => {
    const nextValue = field === 'cuit' ? formatCuit(value) : value;
    setForm((prev) => ({ ...prev, [field]: nextValue }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim()) {
      setMessage('Completa el nombre del regional.');
      setMessageType('error');
      return;
    }
    if (!form.sigla.trim()) {
      setMessage('Completa la sigla del regional.');
      setMessageType('error');
      return;
    }
    if (!/^[A-Za-z0-9]+$/.test(form.sigla.trim())) {
      setMessage('La sigla solo puede contener letras y números.');
      setMessageType('error');
      return;
    }
    if (!form.cuit.trim()) {
      setMessage('Completa el CUIT del regional.');
      setMessageType('error');
      return;
    }
    if (!/^\d{2}-\d{8}-\d$/.test(form.cuit.trim())) {
      setMessage('El CUIT debe tener el formato 20-33455684-2.');
      setMessageType('error');
      return;
    }

    setIsSubmitting(true);
    setMessage('Creando regional...');
    setMessageType('info');
    try {
      await regionalApiService.createRegional({
        nombre: form.nombre,
        sigla: form.sigla,
        cuit: form.cuit,
        descripcion: form.descripcion,
        telefono: form.telefono,
        email: form.email,
        responsableNombre: form.responsableNombre,
        responsableApellido: form.responsableApellido,
        responsableEmail: form.responsableEmail,
        apiKeyOpenAI: form.apiKeyOpenAI,
        apiKeyRagFlow: form.apiKeyRagFlow,
        direccionCalle: form.direccionCalle,
        direccionNumero: form.direccionNumero,
        ciudad: form.ciudad,
        provincia: form.provincia,
        codigoPostal: form.codigoPostal
      });
      setMessage('Regional creado correctamente.');
      setMessageType('success');
      setTimeout(() => navigate('/admin-dashboard', { replace: true, state: { tab: 'regionales' } }), 900);
    } catch (error) {
      setMessage(error.message || 'No pudimos crear el regional.');
      setMessageType('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="admin-standalone">
      <header className="admin-standalone-header">
        <div>
          <p className="eyebrow">Administración general</p>
          <h1>Alta de regional</h1>
          <p className="subtitle">Completa el formulario con todos los datos disponibles del regional.</p>
        </div>
        <div className="admin-standalone-actions">
          <ActiveRegionalBadge />
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/admin/select-regional')}
          >
            <i className="fas fa-arrow-left" />
            <span>Volver a selección de regionales</span>
          </button>
          <button className="user-menu-trigger" onClick={logout}>
            <div className="user-avatar">
              <i className="fas fa-user-shield"></i>
            </div>
            <i className="fas fa-sign-out-alt"></i>
          </button>
        </div>
      </header>

      <div className="admin-dual-panel">
        <div className="content-card">
          <div className="card-header">
            <div className="card-title">
              <i className="fas fa-globe" />
              <span>Datos del regional</span>
            </div>
          </div>
          <div className="card-content">
            <p style={{ marginTop: 0, color: '#475569' }}>
              Los campos obligatorios son Nombre, Sigla y CUIT. El resto son opcionales.
            </p>
            <form onSubmit={handleSubmit} className="prof-create-form">
              <div className="form-grid">
                <div className="form-field">
                  <label>Nombre *</label>
                  <input
                    type="text"
                    value={form.nombre}
                    onChange={(e) => handleChange('nombre', e.target.value)}
                    placeholder="Regional X"
                  />
                </div>
                <div className="form-field">
                  <label>Sigla *</label>
                  <input
                    type="text"
                    value={form.sigla}
                    onChange={(e) => handleChange('sigla', e.target.value.toUpperCase())}
                    placeholder="FRC"
                    maxLength={8}
                  />
                </div>
                <div className="form-field">
                  <label>CUIT *</label>
                  <input
                    type="text"
                    value={form.cuit}
                    onChange={(e) => handleChange('cuit', e.target.value)}
                    inputMode="numeric"
                    pattern="\d{2}-\d{8}-\d"
                    placeholder="20-33455684-2"
                  />
                </div>
              </div>

              <div className="form-field">
                <label>Descripción</label>
                <textarea
                  value={form.descripcion}
                  onChange={(e) => handleChange('descripcion', e.target.value)}
                  placeholder="Descripción opcional"
                  style={{ borderRadius: '10px', border: '1px solid var(--gray-200)', padding: '0.65rem 0.85rem', fontSize: '0.95rem', minHeight: '80px' }}
                />
              </div>

              <div className="form-grid">
                <div className="form-field">
                  <label>Teléfono</label>
                  <input
                    type="text"
                    value={form.telefono}
                    onChange={(e) => handleChange('telefono', e.target.value)}
                    placeholder="351-555-1122"
                  />
                </div>
                <div className="form-field">
                  <label>Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="contacto@regional.edu.ar"
                  />
                </div>
              </div>

              <div className="form-field">
                <label>API Key OpenAI</label>
                <input
                  type="text"
                  value={form.apiKeyOpenAI}
                  onChange={(e) => handleChange('apiKeyOpenAI', e.target.value)}
                  placeholder="sk-..."
                />
              </div>

              <div className="form-field">
                <label>API Key AVE</label>
                <input
                  type="text"
                  value={form.apiKeyRagFlow}
                  onChange={(e) => handleChange('apiKeyRagFlow', e.target.value)}
                  placeholder="ave-..."
                />
              </div>

              <div className="form-grid">
                <div className="form-field">
                  <label>Responsable - Nombre</label>
                  <input
                    type="text"
                    value={form.responsableNombre}
                    onChange={(e) => handleChange('responsableNombre', e.target.value)}
                    placeholder="Nombre responsable"
                  />
                </div>
                <div className="form-field">
                  <label>Responsable - Apellido</label>
                  <input
                    type="text"
                    value={form.responsableApellido}
                    onChange={(e) => handleChange('responsableApellido', e.target.value)}
                    placeholder="Apellido responsable"
                  />
                </div>
              </div>

              <div className="form-field">
                <label>Responsable - Email</label>
                <input
                  type="email"
                  value={form.responsableEmail}
                  onChange={(e) => handleChange('responsableEmail', e.target.value)}
                  placeholder="responsable@regional.edu.ar"
                />
              </div>

              <div className="form-grid">
                <div className="form-field">
                  <label>Dirección - Calle</label>
                  <input
                    type="text"
                    value={form.direccionCalle}
                    onChange={(e) => handleChange('direccionCalle', e.target.value)}
                    placeholder="Av. Libertad"
                  />
                </div>
                <div className="form-field">
                  <label>Dirección - Número</label>
                  <input
                    type="text"
                    value={form.direccionNumero}
                    onChange={(e) => handleChange('direccionNumero', e.target.value)}
                    placeholder="1540"
                  />
                </div>
              </div>

              <div className="form-grid">
                <div className="form-field">
                  <label>Ciudad</label>
                  <input
                    type="text"
                    value={form.ciudad}
                    onChange={(e) => handleChange('ciudad', e.target.value)}
                    placeholder="Córdoba"
                  />
                </div>
                <div className="form-field">
                  <label>Provincia</label>
                  <input
                    type="text"
                    value={form.provincia}
                    onChange={(e) => handleChange('provincia', e.target.value)}
                    placeholder="Córdoba"
                  />
                </div>
              </div>

              <div className="form-field">
                <label>Código Postal</label>
                <input
                  type="text"
                  value={form.codigoPostal}
                  onChange={(e) => handleChange('codigoPostal', e.target.value)}
                  placeholder="5000"
                />
              </div>

              {message && (
                <div
                  className="modal-message"
                  style={{
                    marginTop: '0.5rem',
                    borderColor: messageType === 'success' ? '#bbf7d0' : messageType === 'error' ? '#fecaca' : 'var(--gray-200)',
                    background: messageType === 'success' ? '#dcfce7' : messageType === 'error' ? '#fee2e2' : '#f8fafc',
                    color: messageType === 'error' ? '#7f1d1d' : messageType === 'success' ? '#0f5132' : 'var(--gray-800)'
                  }}
                >
                  {isSubmitting && <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.35rem' }} />}
                  {message}
                </div>
              )}

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => navigate('/admin/select-regional')}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-save" />}
                  <span>Crear regional</span>
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="content-card">
          <div className="card-header">
            <div className="card-title">
              <i className="fas fa-school" />
              <span>Regionales existentes</span>
            </div>
          </div>
          <div className="card-content">
            {regionalesLoading && <p className="state">Cargando regionales...</p>}
            {regionalesError && <p className="state error">{regionalesError}</p>}
            {!regionalesLoading && !regionalesError && regionales.length === 0 && (
              <p className="state">Todavía no hay regionales creados.</p>
            )}
            {!regionalesLoading && !regionalesError && regionales.length > 0 && (
              <div className="admin-list">
                {regionales.map((reg) => {
                  const regionalId = reg.id || reg.Id;
                  return (
                    <div key={regionalId || reg.nombre} className="admin-list-item">
                      <div>
                        <strong>{reg.nombre || reg.name || 'Sin nombre'}</strong>
                        <p style={{ margin: '0.2rem 0 0', color: '#64748b' }}>{reg.descripcion || reg.detalle || '-'}</p>
                      </div>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => regionalId && navigate(`/admin/regionales/${regionalId}/editar`, { state: { regional: reg } })}
                        disabled={!regionalId}
                      >
                        Editar
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCreateRegional;
